import { useEffect, useMemo, useState } from "react";
import { config } from "../../../../../utils/config";
import {
  useFileStatus,
  useInvalidateFileStatus,
} from "./useLiquidationQueries";

export interface LiquidationConfig {
  docType: string;
  dbName: string;
}

const LIQUIDATION_CONFIGS: Record<string, LiquidationConfig> = {
  xama_prod: {
    docType: "transfeera_creditos_liquidados",
    dbName: "xama_prod",
  },
  xama_dev: {
    docType: "transfeera_creditos_liquidados",
    dbName: "xama_dev",
  },
};

const MAX_REFRESH_COUNT = 15;

export function useLiquidation() {
  const selectedFund = `xama_${config.stage}`;
  const [refreshCount, setRefreshCount] = useState(0);
  const invalidateFileStatus = useInvalidateFileStatus();

  const liquidationConfig = useMemo(() => {
    return selectedFund ? LIQUIDATION_CONFIGS[selectedFund] || null : null;
  }, [selectedFund]);

  const datetime = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const fileStatusParams = useMemo(() => {
    if (!liquidationConfig) return null;
    return {
      datetime,
      docType: liquidationConfig.docType,
      dbName: liquidationConfig.dbName,
    };
  }, [liquidationConfig, datetime]);

  const shouldEnableQuery = Boolean(
    fileStatusParams && refreshCount < MAX_REFRESH_COUNT
  );

  const {
    data: fileStatusData = [],
    isLoading: fileStatusLoading,
    isFetching: fileStatusFetching,
  } = useFileStatus(
    fileStatusParams || { datetime: "", docType: "", dbName: "" },
    shouldEnableQuery
  );

  useEffect(() => {
    if (!shouldEnableQuery) return;

    const interval = setInterval(() => {
      setRefreshCount((prev) => {
        if (prev < MAX_REFRESH_COUNT - 1) {
          return prev + 1;
        }
        return prev;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [shouldEnableQuery]);

  const getLiquidationConfig = (fundKey: string): LiquidationConfig | null => {
    return LIQUIDATION_CONFIGS[fundKey] || null;
  };



  // O arquivo não aparece na lista no instante do upload: o s3Upload só grava
  // em parking_lot, e quem insere em processed_files — a tabela que o
  // fileStatus lê — é o s3Copy, uns 10s depois. Nessa janela a lista continua
  // do mesmo tamanho e a tela mostrava "Nenhum arquivo processado hoje", que se
  // lê como falha do upload. Guarda o tamanho da lista no momento do envio e
  // considera que ainda está chegando enquanto ela não cresce.
  const [contagemAoSubir, setContagemAoSubir] = useState<number | null>(null);

  useEffect(() => {
    if (contagemAoSubir !== null && fileStatusData.length > contagemAoSubir) {
      setContagemAoSubir(null);
    }
  }, [fileStatusData.length, contagemAoSubir]);

  // Rede de segurança: se o arquivo nunca aparecer, não deixa a tela esperando
  // para sempre — volta ao estado normal e o usuário vê que nada chegou.
  useEffect(() => {
    if (contagemAoSubir === null) return;
    const timeout = setTimeout(() => setContagemAoSubir(null), 90000);
    return () => clearTimeout(timeout);
  }, [contagemAoSubir]);

  const resetRefresh = () => {
    setRefreshCount(0);
    setContagemAoSubir(fileStatusData.length);
    invalidateFileStatus();
  };

  const calculateErrorPercentage = () => {
    if (fileStatusData.length === 0) return 0;
    return (
      fileStatusData.filter((item) => item.status === "error").length /
      fileStatusData.length
    );
  };

  const calculateSuccessPercentage = () => {
    if (fileStatusData.length === 0) return 0;
    return (
      fileStatusData.filter((item) => item.status === "success").length /
      fileStatusData.length
    );
  };

  const getErrorCount = () => {
    return fileStatusData.filter((item) => item.status === "error").length;
  };

  const getSuccessCount = () => {
    return fileStatusData.filter((item) => item.status === "success").length;
  };

  return {
    funds: [],
    selectedFund,
    fileStatusData,
    loading: fileStatusLoading && fileStatusData.length === 0,
    isLoadingFunds: false,
    isFetching: fileStatusFetching,
    error: null,
    getLiquidationConfig,
    resetRefresh,
    calculateErrorPercentage,
    calculateSuccessPercentage,
    getErrorCount,
    getSuccessCount,
    refreshCount,
    /** Upload aceito, aguardando o arquivo aparecer em processed_files */
    aguardandoProcessamento: contagemAoSubir !== null,
  };
}
